import React, { Fragment, useCallback, useEffect, useState } from "react";
import BottomSheet, {
  BottomSheetBackdrop,
  BottomSheetBackdropProps,
  BottomSheetFlatList,
  BottomSheetView,
} from "@gorhom/bottom-sheet";
import { Pressable, useWindowDimensions, View, Text } from "react-native";
import { useSheet } from "../shared/sheet-provider";
import { CheckboxInput } from "../ui/checkbox-input";
import { RadioInput } from "../ui/radio-input";
import CloseIcon from "@/assets/svgs/close-icon";
import { remapProps } from "react-native-css-interop";
import { SheetIds } from "../utils/sheet-ids";
import useResponsiveWidth from "@/elements/hooks/use-responsive-width";

type Option = {
  label: string;
  value: string | number;
  disabled?: boolean;
};

type Options = Option[];

remapProps(BottomSheetView, {
  className: "style",
});

remapProps(BottomSheet, {
  className: "containerStyle",
});

export type SingleSelectSheetPayload = {
  title?: string;
  subtitle?: string;
  options: Option[];
  preSelectedOption: string | number;
  uiMode?: "checkbox" | "radio";
  keepOriginalOrder?: boolean;
};

export type SingleSelectSheetResult = {
  selectedOption?: string | number;
};

function SingleSelectSheet() {
  const [shouldRender, setShouldRender] = useState(false);
  const { register, close, dismiss, sheet } = useSheet<
    SingleSelectSheetPayload,
    SingleSelectSheetResult
  >(SheetIds.SINGLE_SELECT);
  const { payload } = sheet ?? {};
  const uiMode = payload?.uiMode ?? "checkbox";
  const title = payload?.title ?? "";
  const subtitle = payload?.subtitle ?? "";
  const preSelectedOption = payload?.preSelectedOption;
  const keepOriginalOrder = payload?.keepOriginalOrder ?? true;
  const [selectedOption, setSelectedOption] = useState<
    string | number | undefined
  >(preSelectedOption);
  const rawOptions: Options = payload?.options || [];

  useEffect(() => {
    setSelectedOption(preSelectedOption);
  }, [preSelectedOption]);

  const options: Options = React.useMemo(() => {
    if (
      keepOriginalOrder ||
      preSelectedOption === "" ||
      preSelectedOption === undefined
    )
      return rawOptions;

    const selectedItem = rawOptions.find(
      (option) => option.value === preSelectedOption
    );
    const otherItems = rawOptions.filter(
      (option) => option.value !== preSelectedOption
    );

    if (selectedItem) {
      return [selectedItem, ...otherItems];
    }
    return rawOptions;
  }, [rawOptions, preSelectedOption, keepOriginalOrder]);
  const responsiveWidth = useResponsiveWidth();
  const { width } = useWindowDimensions();
  const marginLeft = (width - responsiveWidth) / 2;

  useEffect(() => {
    setShouldRender(true);
  }, []);

  const setBottomSheetRef = useCallback(
    (ref: BottomSheet | null) => {
      if (ref) {
        const refObject = { current: ref };
        register(refObject);
      }
    },
    [register]
  );

  const handleSheetChanges = useCallback(
    (index: number) => {
      if (index === -1) {
        dismiss();
      }
    },
    [dismiss]
  );

  const onClose = useCallback(() => {
    close({
      selectedOption,
    });
  }, [close, selectedOption]);

  const onSelect = useCallback(
    (value: string | number) => {
      close({
        selectedOption: value,
      });
    },
    [close]
  );

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        disappearsOnIndex={-1}
        appearsOnIndex={0}
        onPress={onClose}
      />
    ),
    [onClose]
  );

  const renderItem = useCallback(
    ({ item }: { item: Option }) => {
      const onPress = () => {
        setSelectedOption(item.value);
        onSelect(item.value);
      };

      return (
        <View className="mx-5">
          {uiMode === "checkbox" ? (
            <CheckboxInput onPress={onPress} disabled={item.disabled}>
              <CheckboxInput.Label>{item.label}</CheckboxInput.Label>
              <CheckboxInput.Icon checked={selectedOption === item.value} />
            </CheckboxInput>
          ) : (
            <RadioInput onPress={onPress} disabled={item.disabled}>
              <RadioInput.Label>{item.label}</RadioInput.Label>
              <RadioInput.Icon checked={selectedOption === item.value} />
            </RadioInput>
          )}
        </View>
      );
    },
    [onSelect, selectedOption, uiMode]
  );

  return shouldRender ? (
    <BottomSheet
      ref={setBottomSheetRef}
      backdropComponent={renderBackdrop}
      enablePanDownToClose
      index={-1}
      handleComponent={() => <Fragment />}
      onChange={handleSheetChanges}
      backgroundStyle={{
        backgroundColor: "#FCF9F5",
      }}
      style={{ width: responsiveWidth, marginLeft: marginLeft }}
    >
      <BottomSheetFlatList
        data={options}
        renderItem={renderItem}
        ItemSeparatorComponent={() => <View className="h-2" />}
        ListFooterComponent={() =>
          options.length <= 2 ? (
            <View className="pb-safe min-h-[50]" />
          ) : (
            <View className="h-2 pb-safe min-h-[50]" />
          )
        }
        ListHeaderComponent={() => (
          <View className="bg-teachLoop-creamBase rounded-t-[20]">
            <View className="h-6" />
            <View className="mx-5 flex-row">
              <Text className="font-geologica-medium text-black text-xl leading-[1.5] flex-1">
                {title}
              </Text>
              <Pressable onPress={onClose} className="active:opacity-80">
                <CloseIcon
                  className="size-6"
                  pathClassName="stroke-teachLoop-textPrimary"
                />
              </Pressable>
            </View>
            {subtitle && (
              <View className="mx-5 pt-2">
                <Text className="font-geologica-regular text-black text-sm leading-[1.5]">
                  {subtitle}
                </Text>
              </View>
            )}
            <View className="h-6" />
          </View>
        )}
        stickyHeaderIndices={[0]}
      />
    </BottomSheet>
  ) : null;
}

export default SingleSelectSheet;
